import React, { useRef } from 'react'
import { motion } from 'motion/react'
import { type Photo, isJsonapiPhoto } from '@app-types/entity_types'
import type { DerivedMarker } from '../../types/derived_items'
import { createLogger } from '@utils/logger'
import { useGesture } from '../GestureProvider'
import type { DragEventData } from '../../types/gesture-types'
import { SPRING_TRANSITION } from '../../constants'
import DefaultMarkerContent from './DefaultMarkerContent'
import { useViewContext } from '@base/ViewContext'

const log = createLogger('Marker')
log.setLevel('info')

type MarkerProps = {
  marker: DerivedMarker
  photoData: Photo | null
  x: number
  y: number
  zIndex: number
  width: number
  height: number
  onTap: (id: number | string, type: 'marker', bounds: DOMRect) => void
  onPanStart: (event: DragEventData) => void
  onPanMove: (event: DragEventData) => void
  onPanEnd: (event: DragEventData) => void
}

export interface MarkerContentProps {
  marker: DerivedMarker
  photoData: Photo | null
  imageComponent: React.ReactNode
}

interface ViewContextValue {
  componentMap?: {
    MarkerContent?: React.ComponentType<MarkerContentProps>
  }
}

const Marker: React.FC<MarkerProps> = props => {
  const { marker, photoData, x, y, width, height, zIndex, onTap, onPanStart, onPanMove, onPanEnd } = props

  const viewContext = useViewContext() as ViewContextValue

  const ref = useRef<HTMLDivElement | null>(null)
  const { bindGesture, isDragging } = useGesture()

  // Use the gesture context to bind gesture handlers
  const bind = bindGesture(
    {
      onDragStart: ({ event, deltaX, deltaY }) => {
        onPanStart({ event, deltaX, deltaY })
      },
      onDragMove: ({ event, deltaX, deltaY }) => {
        onPanMove({ event, deltaX, deltaY })
      },
      onDragEnd: ({ event, deltaX, deltaY }) => {
        onPanEnd({ event, deltaX, deltaY })
      },
      onTap: () => {
        if (!isDragging && ref.current) {
          const bounds = ref.current.getBoundingClientRect()
          // Pass id, type 'marker', viewport-relative bounds
          onTap(marker.id, 'marker', bounds)
        }
      },
    },
    {
      axis: 'x',
    }
  )

  const processedImageAvailable = () => {
    if (!photoData) return false
    // Could be a plain rest object or a JSON::API object
    const photoAttributes = photoData && isJsonapiPhoto(photoData) ? photoData.attributes : photoData
    return photoAttributes && photoAttributes.image_processing_complete === true
  }

  const timelineItemStyle: React.CSSProperties = {
    width,
    height,
    transform: `translate(${x}px, 0px)`, // Only apply x here, y is positioned by animation
    userSelect: isDragging ? 'none' : 'auto',
  }

  const showImage = processedImageAvailable()
  let imageContainerWidth = 0
  let imageContainerHeight = 0
  let imageData = null

  const photoAttributes = photoData && isJsonapiPhoto(photoData) ? photoData.attributes : photoData

  if (showImage && photoAttributes) {
    imageData = photoAttributes?.image_urls?.small_square
    imageContainerWidth = 45 // Standard small image size
    imageContainerHeight = 45
  }

  let imageComponent = null
  if (showImage && imageData && photoAttributes) {
    const imageWrapperStyle: React.CSSProperties = {
      width: imageContainerWidth,
      height: imageContainerHeight,
    }
    imageComponent = (
      <div style={imageWrapperStyle} className="marker-image-wrapper">
        <img src={imageData.src} alt={marker.title || 'Event image'} />
      </div>
    )
  }

  const MarkerContentComponent = viewContext.componentMap?.MarkerContent || DefaultMarkerContent

  return (
    <motion.div initial={false} animate={{ y }} transition={SPRING_TRANSITION} style={{ position: 'relative', zIndex }}>
      <div
        ref={ref}
        className="timeline-item timeline-item--marker z-depth-1"
        style={timelineItemStyle}
        key={`marker-${marker.id}`}
        {...bind()}
      >
        <MarkerContentComponent marker={marker} photoData={photoData} imageComponent={imageComponent} />
      </div>
    </motion.div>
  )
}

export default Marker
